import React, { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { 
  Bell, 
  BellOff, 
  Settings as SettingsIcon, 
  Activity,
  DollarSign,
  CreditCard,
  Volume2,
  VolumeX,
  Vibrate,
  Smartphone,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Badge as BadgeIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useNotificationSettingsStore } from '../../store/notificationSettingsStore';
import { useAuthStore } from '@flows.cash/core';
import type { NotificationSettings } from '@flows.cash/core';

export default function NotificationSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { 
    settings, 
    loadSettings, 
    updateSettings, 
    isLoading, 
    error 
  } = useNotificationSettingsStore();

  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    if (user?.id) {
      loadSettings();
    }
  }, [user?.id]); // Remove loadSettings from dependencies to prevent infinite loop

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSettingChange = async (key: keyof NotificationSettings, value: boolean) => {
    if (!user?.id) return;

    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);

    try {
      await updateSettings({ [key]: value });
    } catch (error) {
      console.error('Failed to update notification settings:', error);
      // Revert on error
      setLocalSettings(localSettings);
    }
  };

  const renderToggleRow = (
    key: keyof NotificationSettings,
    label: string,
    description: string,
    icon: React.ReactNode,
    value: boolean
  ) => {
    return (
      <div className="flex items-center justify-between py-4 border-b border-border dark:border-border-dark last:border-b-0">
        <div className="flex items-start space-x-3 flex-1">
          <div className="flex-shrink-0 mt-1">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
              {label}
            </div>
            <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
              {description}
            </div>
          </div>
        </div>
        <Switch
          checked={value}
          onCheckedChange={(checked) => handleSettingChange(key, checked)}
          disabled={isLoading}
        />
      </div>
    );
  };

  const renderSection = (title: string, icon: React.ReactNode, items: Array<{
    key: keyof NotificationSettings;
    label: string;
    description: string;
  }>) => {
    return (
      <Card className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-text-primary dark:text-text-dark-primary">
            <span className="text-secondary dark:text-primary-dark">{icon}</span>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          {items.map((item) => renderToggleRow(
            item.key,
            item.label,
            item.description,
            <div className="w-4 h-4 rounded-full bg-secondary/20 dark:bg-primary-dark/20 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-secondary dark:bg-primary-dark" />
            </div>,
            localSettings?.[item.key as keyof NotificationSettings] || false
          ))}
        </CardContent>
      </Card>
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {t('notificationSettings.loadError')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            {t('notificationSettings.title')}
          </h1>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {t('notificationSettings.description')}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary dark:border-primary-dark"></div>
        </div>
      )}

      {/* Settings Sections */}
      {!isLoading && (
        <div className="space-y-6">
          {/* Transaction Notifications */}
          {renderSection(
            t('notificationSettings.transactions.title'),
            <CreditCard className="h-5 w-5" />,
            [
              {
                key: 'transactionCreated',
                label: t('notificationSettings.transactions.created'),
                description: t('notificationSettings.transactions.createdDesc')
              },
              {
                key: 'transactionDeleted',
                label: t('notificationSettings.transactions.deleted'),
                description: t('notificationSettings.transactions.deletedDesc')
              },
              {
                key: 'largeTransactions',
                label: t('notificationSettings.transactions.large'),
                description: t('notificationSettings.transactions.largeDesc')
              },
              {
                key: 'recurringTransactions',
                label: t('notificationSettings.transactions.recurring'),
                description: t('notificationSettings.transactions.recurringDesc')
              },
              {
                key: 'categoryChanged',
                label: t('notificationSettings.transactions.categoryChanged'),
                description: t('notificationSettings.transactions.categoryChangedDesc')
              },
              {
                key: 'transferComplete',
                label: t('notificationSettings.transactions.transferComplete'),
                description: t('notificationSettings.transactions.transferCompleteDesc')
              }
            ]
          )}

          {/* System Notifications */}
          {renderSection(
            t('notificationSettings.system.title'),
            <SettingsIcon className="h-5 w-5" />,
            [
              {
                key: 'exchangeRateUpdates',
                label: t('notificationSettings.system.exchangeRate'),
                description: t('notificationSettings.system.exchangeRateDesc')
              },
              {
                key: 'newFeatures',
                label: t('notificationSettings.system.newFeature'),
                description: t('notificationSettings.system.newFeatureDesc')
              },
              {
                key: 'appUpdates',
                label: t('notificationSettings.system.update'),
                description: t('notificationSettings.system.updateDesc')
              },
              {
                key: 'securityAlerts',
                label: t('notificationSettings.system.security'),
                description: t('notificationSettings.system.securityDesc')
              },
              {
                key: 'syncStatus',
                label: t('notificationSettings.system.sync'),
                description: t('notificationSettings.system.syncDesc')
              }
            ]
          )}

          {/* Budget Notifications */}
          {renderSection(
            t('notificationSettings.budgets.title'),
            <DollarSign className="h-5 w-5" />,
            [
              {
                key: 'budgetApproaching',
                label: t('notificationSettings.budgets.approaching'),
                description: t('notificationSettings.budgets.approachingDesc')
              },
              {
                key: 'budgetExceeded',
                label: t('notificationSettings.budgets.exceeded'),
                description: t('notificationSettings.budgets.exceededDesc')
              },
              {
                key: 'budgetReset',
                label: t('notificationSettings.budgets.reset'),
                description: t('notificationSettings.budgets.resetDesc')
              }
            ]
          )}

          {/* Display & Delivery Settings */}
          {renderSection(
            t('notificationSettings.display.title'),
            <Bell className="h-5 w-5" />,
            [
              {
                key: 'showInApp',
                label: t('notificationSettings.display.inApp'),
                description: t('notificationSettings.display.inAppDesc')
              },
              {
                key: 'pushNotifications',
                label: t('notificationSettings.display.push'),
                description: t('notificationSettings.display.pushDesc')
              },
              {
                key: 'soundEnabled',
                label: t('notificationSettings.display.sound'),
                description: t('notificationSettings.display.soundDesc')
              },
              {
                key: 'vibrationEnabled',
                label: t('notificationSettings.display.vibration'),
                description: t('notificationSettings.display.vibrationDesc')
              }
            ]
          )}

          {/* Status Overview */}
          <Card className="bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm bg-gradient-to-br from-secondary/5 to-primary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-text-primary dark:text-text-dark-primary">
                <Activity className="h-5 w-5 text-secondary dark:text-primary-dark" />
                {t('notificationSettings.status.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  {localSettings?.showInApp ? (
                    <Eye className="h-4 w-4 text-positive dark:text-positive-dark" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                  )}
                  <span className="text-sm text-text-primary dark:text-text-dark-primary">
                    {t('notificationSettings.display.inApp')}
                  </span>
                  <Badge variant={localSettings?.showInApp ? "default" : "secondary"} className="text-xs">
                    {localSettings?.showInApp ? t('actions.enabled') : t('actions.disabled')}
                  </Badge>
                </div>
                
                <div className="flex items-center space-x-2">
                  {localSettings?.pushNotifications ? (
                    <Smartphone className="h-4 w-4 text-positive dark:text-positive-dark" />
                  ) : (
                    <Smartphone className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                  )}
                  <span className="text-sm text-text-primary dark:text-text-dark-primary">
                    {t('notificationSettings.display.push')}
                  </span>
                  <Badge variant={localSettings?.pushNotifications ? "default" : "secondary"} className="text-xs">
                    {localSettings?.pushNotifications ? t('actions.enabled') : t('actions.disabled')}
                  </Badge>
                </div>
                
                <div className="flex items-center space-x-2">
                  {localSettings?.soundEnabled ? (
                    <Volume2 className="h-4 w-4 text-positive dark:text-positive-dark" />
                  ) : (
                    <VolumeX className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                  )}
                  <span className="text-sm text-text-primary dark:text-text-dark-primary">
                    {t('notificationSettings.display.sound')}
                  </span>
                  <Badge variant={localSettings?.soundEnabled ? "default" : "secondary"} className="text-xs">
                    {localSettings?.soundEnabled ? t('actions.enabled') : t('actions.disabled')}
                  </Badge>
                </div>
                
                <div className="flex items-center space-x-2">
                  {localSettings?.vibrationEnabled ? (
                    <Vibrate className="h-4 w-4 text-positive dark:text-positive-dark" />
                  ) : (
                    <Vibrate className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                  )}
                  <span className="text-sm text-text-primary dark:text-text-dark-primary">
                    {t('notificationSettings.display.vibration')}
                  </span>
                  <Badge variant={localSettings?.vibrationEnabled ? "default" : "secondary"} className="text-xs">
                    {localSettings?.vibrationEnabled ? t('actions.enabled') : t('actions.disabled')}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
} 